// utils/extractMessageText.js

const extractMessageText = (msg) => {
    const messageContent = msg?.message;
    if (!messageContent) return "";

    if (messageContent.conversation) return messageContent.conversation;
    if (messageContent.extendedTextMessage?.text)
        return messageContent.extendedTextMessage.text;

    if (messageContent.imageMessage?.caption)
        return messageContent.imageMessage.caption;
    if (messageContent.videoMessage?.caption)
        return messageContent.videoMessage.caption;
    if (messageContent.documentMessage?.caption)
        return messageContent.documentMessage.caption;
    if (messageContent.audioMessage?.caption)
        return messageContent.audioMessage.caption;
    if (messageContent.stickerMessage?.caption)
        return messageContent.stickerMessage.caption;

    if (messageContent.imageMessage) return "[Image message]";
    if (messageContent.videoMessage) return "[Video message]";
    if (messageContent.audioMessage) return "[Audio message]";
    if (messageContent.documentMessage)
        return `[Document: ${
            messageContent.documentMessage.fileName || "Unknown"
        }]`;
    if (messageContent.stickerMessage) return "[Sticker message]";

    if (messageContent.locationMessage) {
        const {
            degreesLatitude: lat,
            degreesLongitude: lng,
            name,
        } = messageContent.locationMessage;
        return `[Location${name ? `: ${name}` : ""} (${lat}, ${lng})]`;
    }

    if (messageContent.liveLocationMessage) {
        const { degreesLatitude: lat, degreesLongitude: lng } =
            messageContent.liveLocationMessage;
        return `[Live Location (${lat}, ${lng})]`;
    }

    if (messageContent.contactMessage) {
        return `[Contact: ${
            messageContent.contactMessage.displayName || "Unknown"
        }]`;
    }

    if (messageContent.contactsArrayMessage) {
        const count = messageContent.contactsArrayMessage.contacts?.length || 0;
        return `[${count} Contact${count !== 1 ? "s" : ""}]`;
    }

    if (messageContent.buttonsResponseMessage?.selectedButtonId)
        return messageContent.buttonsResponseMessage.selectedButtonId;
    if (messageContent.templateButtonReplyMessage?.selectedId)
        return messageContent.templateButtonReplyMessage.selectedId;
    if (messageContent.listResponseMessage?.singleSelectReply?.selectedRowId)
        return messageContent.listResponseMessage.singleSelectReply
            .selectedRowId;
    if (messageContent.interactiveResponseMessage?.buttonReply?.id)
        return messageContent.interactiveResponseMessage.buttonReply.id;
    if (messageContent.interactiveResponseMessage?.listReply?.id)
        return messageContent.interactiveResponseMessage.listReply.id;

    if (messageContent.reactionMessage?.text) {
        const emoji = messageContent.reactionMessage.text;
        const targetKey = messageContent.reactionMessage.key?.id;
        return `[Reacted with: ${emoji}${
            targetKey ? ` to message ${targetKey}` : ""
        }]`;
    }

    if (messageContent.pollCreationMessage)
        return `[Poll: ${
            messageContent.pollCreationMessage.name || "Unknown question"
        }]`;
    if (messageContent.pollUpdateMessage) return "[Poll vote update]";
    if (messageContent.groupInviteMessage)
        return `[Group invite: ${
            messageContent.groupInviteMessage.groupName || "Unknown group"
        }]`;

    if (messageContent.call)
        return `[${messageContent.call.callType || "call"} call]`;

    if (messageContent.protocolMessage) {
        const type = messageContent.protocolMessage.type;
        switch (type) {
            case "REVOKE":
                return "[Message deleted]";
            case "EPHEMERAL_SETTING":
                return "[Disappearing messages setting changed]";
            default:
                return `[System message: ${type}]`;
        }
    }

    if (messageContent.encReactionMessage) return "[Encrypted reaction]";
    if (messageContent.requestPhoneNumberMessage)
        return "[Phone number request]";

    if (messageContent.templateMessage) {
        const template = messageContent.templateMessage.hydratedTemplate;
        if (template?.hydratedContentText) return template.hydratedContentText;
        return "[Template message]";
    }

    if (messageContent.highlyStructuredMessage)
        return "[Highly structured message]";
    if (messageContent.invoiceMessage) return "[Invoice message]";
    if (messageContent.productMessage)
        return `[Product: ${
            messageContent.productMessage.product?.title || "Unknown"
        }]`;
    if (messageContent.orderMessage) return "[Order message]";
    if (messageContent.sendPaymentMessage) return "[Payment sent]";
    if (messageContent.requestPaymentMessage) return "[Payment requested]";
    if (messageContent.declinePaymentRequestMessage)
        return "[Payment declined]";
    if (messageContent.cancelPaymentRequestMessage)
        return "[Payment cancelled]";
    if (messageContent.liveLocationMessage) return "[Live location shared]";
    if (messageContent.stickerMessage) return "[Sticker]";
    if (messageContent.gifMessage)
        return messageContent.gifMessage.caption || "[GIF]";

    if (messageContent.viewOnceMessage) {
        const content = messageContent.viewOnceMessage.message;
        if (content?.imageMessage) return "[View once image]";
        if (content?.videoMessage) return "[View once video]";
        return "[View once message]";
    }

    if (messageContent.ephemeralMessage) {
        return extractMessageText({
            message: messageContent.ephemeralMessage.message,
        });
    }

    if (messageContent.deviceSentMessage) {
        return extractMessageText({
            message: messageContent.deviceSentMessage.message,
        });
    }

    if (messageContent.messageContextInfo) return "[Context info message]";
    if (messageContent.pinInChatMessage) return "[Pin in chat]";
    if (messageContent.keepInChatMessage) return "[Keep in chat]";
    if (messageContent.eventMessage) return "[Event message]";
    if (messageContent.commentMessage) return "[Comment message]";
    if (messageContent.newsletterAdminInviteMessage)
        return "[Newsletter admin invite]";

    if (messageContent.interactiveMessage) {
        const header = messageContent.interactiveMessage.header?.title;
        const body = messageContent.interactiveMessage.body?.text;
        const footer = messageContent.interactiveMessage.footer?.text;
        let text = "";
        if (header) text += header;
        if (body) text += (text ? "\n" : "") + body;
        if (footer) text += (text ? "\n" : "") + footer;
        return text || "[Interactive message]";
    }

    if (messageContent.botResponseMessage) return "[Bot response]";

    const possibleTextFields = [
        "text",
        "caption",
        "title",
        "name",
        "description",
        "body",
    ];
    for (const field of possibleTextFields) {
        if (
            messageContent[field] &&
            typeof messageContent[field] === "string"
        ) {
            return messageContent[field];
        }
    }

    const messageType = Object.keys(messageContent)[0];
    console.log("Unknown message type:", messageType, messageContent);
    return `[Unsupported message type: ${messageType}]`;
};

export { extractMessageText };
